/**
 * PlaylistUpdatingSplash — full-page playful animation rendered while the
 * playlist + EPG are being fetched / parsed. Picks a random funny status
 * message from a curated list and rotates them every ~2s, with a bouncing
 * TV mascot, animated dots and a progress bar synced to the load lifecycle.
 *
 * Used by PlayerLayout: rendered above the <Outlet> whenever the playlist
 * context is in the "loading" state AND we don't yet have a previous cache
 * to show (so we don't block when a background re-fetch happens).
 */
import React, { useEffect, useMemo, useState } from "react";
import { usePlaylistData } from "../lib/PlaylistDataContext";
import { getLoadProgress, subscribeToLoadProgress } from "../lib/loadProgress";

// Anchored to the channel set users care about, but with playful spin.
// One-liners only — must read like a friendly companion, never serious.
const QUIPS = [
  "Polishing the remote control…",
  "Convincing channels to behave…",
  "Untangling cables (don't ask)…",
  "Stretching the antenna a bit…",
  "Loading 9000 channels of pure joy…",
  "Bribing the satellite with biltong…",
  "Waking up the EPG goblin…",
  "Counting movies, lost count, restarting…",
  "Asking your provider very nicely…",
  "Pixels warming up the couch…",
  "Reticulating splines… (nobody knows what this means)",
  "Aligning the dish like a pro…",
  "Persuading series posters to show up…",
  "Negotiating with the buffering gods…",
  "Brewing some popcorn while we're at it…",
  "Tuning into 1980s static for nostalgia…",
];

// v1.4.21 — Replace the misleading `if (channelsLen > 0) return 92` freeze
// with a MONOTONIC progress curve driven by real signals from the loader.
//
// Phases and their percentage bands:
//   "playlist-snapshot"          →   5 %      (server-side pre-parse check)
//   "download" (no total known)  →  10-45 %   (log-scaled bytes, caps at 45)
//   "parse"                      →  50-88 %   (log-scaled itemsSeen, caps at 88)
//   post-parse enrichment        →  90 %      (Xtream catalog fetch)
//   IDB write / context setState →  95 %
//   status === "ready"           → 100 %
//
// We use a log scale on both bytes and itemsSeen so the bar stays visibly
// moving on huge catalogs (600k channels) without hitting the ceiling too
// early. The ceiling per phase prevents the bar from ever going BACKWARDS
// when the loader transitions to the next phase.
function realProgress(loadProgress, channelsLen, status) {
  if (status === "error") return 100;
  if (status === "ready" || channelsLen > 0) return 100;
  const phase = loadProgress?.phase || "idle";
  const done = loadProgress?.done || 0;
  const bytes = loadProgress?.bytes || 0;
  if (phase === "idle" && !loadProgress?.active) return 5;
  if (phase === "playlist-snapshot") return 8;
  if (phase === "download") {
    if (bytes <= 0) return 10;
    // log10(bytes) mapped to 10 → 45. 100 KB ≈ 21 %, 1 MB ≈ 30 %,
    // 10 MB ≈ 39 %, 100 MB ≈ 45 %.
    const p = 10 + 7 * Math.log10(Math.max(bytes / 1024, 1));
    return Math.min(45, Math.max(10, Math.round(p)));
  }
  if (phase === "parse") {
    if (done <= 0) return 50;
    // log10(done) mapped to 50 → 88. 100 items ≈ 58 %, 1k ≈ 65 %,
    // 10k ≈ 73 %, 100k ≈ 80 %, 600k ≈ 87 %.
    const p = 50 + 7.6 * Math.log10(Math.max(done, 1));
    return Math.min(88, Math.max(50, Math.round(p)));
  }
  // Any other non-idle phase (enrichment, EPG, IDB write) — hold at 92 %.
  return 92;
}

export default function PlaylistUpdatingSplash() {
  const { status, channels, source } = usePlaylistData();
  const [quipIdx, setQuipIdx] = useState(() =>
    Math.floor(Math.random() * QUIPS.length)
  );
  // Real parser progress (channels processed so far) — pushed by
  // `m3uParser.parseM3u` every 5000 items. On huge IPTV playlists this
  // ticks visibly so the user can see the app is alive instead of
  // staring at a frozen splash for a minute.
  const [loadProgress, setLoadProgress] = useState(getLoadProgress);
  useEffect(() => subscribeToLoadProgress(setLoadProgress), []);

  // Rotate a fresh quip every 2.4s for that "the app is alive" feel.
  useEffect(() => {
    if (status !== "loading") return;
    const t = setInterval(() => {
      setQuipIdx((i) => (i + 1 + Math.floor(Math.random() * (QUIPS.length - 1))) % QUIPS.length);
    }, 2400);
    return () => clearInterval(t);
  }, [status]);

  // Don't block the UI when we're showing a cached snapshot — the splash
  // is only for the first load when we have nothing on screen yet.
  const shouldShow = status === "loading" && (channels?.length ?? 0) === 0;

  // Real "items parsed so far" — falls back to context.channels.length
  // for the post-parse / enrichment phase.
  const itemsSeen = loadProgress.active ? loadProgress.done : channels?.length ?? 0;
  // Download bytes — shown in the second tile during the fetch phase so
  // the user sees something tick from the moment the splash appears.
  const bytesSeen = loadProgress.active ? loadProgress.bytes || 0 : 0;
  const phaseLabel =
    loadProgress.phase === "download"
      ? "Downloading playlist…"
      : loadProgress.phase === "parse"
      ? "Parsing playlist…"
      : "Connecting…";

  const progress = useMemo(
    () => realProgress(loadProgress, itemsSeen, status),
    [loadProgress, itemsSeen, status]
  );

  if (!shouldShow) return null;

  return (
    <div
      data-testid="player-playlist-splash"
      role="status"
      aria-live="polite"
      className="vu-splash-bg fixed inset-0 z-[60] flex items-center justify-center"
    >
      {/* Background grid + glow */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute inset-0 opacity-[0.10] [background-image:linear-gradient(rgba(245,2,97,0.6)_1px,transparent_1px),linear-gradient(90deg,rgba(245,2,97,0.6)_1px,transparent_1px)] [background-size:48px_48px]" />
        <div className="absolute -top-32 -left-32 w-[480px] h-[480px] bg-[#6200ee]/30 blur-3xl rounded-full animate-pulse" />
        <div
          className="absolute -bottom-40 -right-40 w-[520px] h-[520px] bg-[#f50261]/25 blur-3xl rounded-full animate-pulse"
          style={{ animationDelay: "1.2s", animationDuration: "4s" }}
        />
      </div>

      <div className="relative max-w-md mx-auto px-6 text-center">
        {/* ZA Player Pro logo with vupro-style circular progress ring */}
        <div className="relative mx-auto w-44 h-44 sm:w-52 sm:h-52">
          {/* Spinning conic-gradient ring (magenta sweep) */}
          <div
            className="absolute inset-0 rounded-full"
            style={{
              background:
                "conic-gradient(from 0deg, transparent 0deg, transparent 270deg, #f50261 360deg)",
              animation: "vupp-spin 1.4s linear infinite",
              mask:
                "radial-gradient(circle, transparent 62%, black 64%, black 100%)",
              WebkitMask:
                "radial-gradient(circle, transparent 62%, black 64%, black 100%)",
            }}
          />
          {/* Static dim ring underneath the spinner */}
          <div
            className="absolute inset-0 rounded-full"
            style={{
              background:
                "radial-gradient(circle, transparent 62%, rgba(255,255,255,0.06) 64%, rgba(255,255,255,0.06) 70%, transparent 72%)",
            }}
          />
          {/* Logo centred inside */}
          <img
            src="/za-logo-512.png"
            alt="ZA Player Pro"
            className="absolute inset-0 m-auto w-[78%] h-[78%] object-contain drop-shadow-[0_0_30px_rgba(245,2,97,0.45)]"
          />
        </div>

        <h1 className="mt-6 text-xl sm:text-2xl font-bold tracking-tight text-white">
          Updating your playlist
        </h1>
        <p
          key={quipIdx}
          className="mt-2 text-white/55 text-sm h-5 animate-[fadeIn_400ms_ease-out]"
        >
          {QUIPS[quipIdx]}
        </p>

        {/* Progress percentage — large, vupro-style */}
        <div className="mt-5 text-3xl font-bold text-white tabular-nums tracking-tight">
          {progress}%
        </div>

        {/* Phase label */}
        <p className="mt-1 text-[10px] text-[#ff4d9d]/90 uppercase tracking-[0.25em]">
          {phaseLabel}
        </p>

        {/* Tiny diagnostic strip — kept compact, vupro-style minimalism */}
        <div className="mt-5 flex items-center justify-center gap-4 text-[11px] text-white/45">
          <span className="tabular-nums">
            <b className="text-white/80">{itemsSeen.toLocaleString()}</b> items
          </span>
          <span className="w-px h-3 bg-white/10" />
          <span className="tabular-nums">
            {bytesSeen > 0
              ? bytesSeen >= 1024 * 1024
                ? `${(bytesSeen / 1024 / 1024).toFixed(1)} MB`
                : `${Math.round(bytesSeen / 1024)} KB`
              : source === "cache"
              ? "From cache"
              : "Streaming"}
          </span>
        </div>

        <p className="mt-7 text-[10px] text-white/30 uppercase tracking-[0.3em]">
          ZA Player Pro
        </p>
      </div>

      {/* Local keyframes — kept here so the component is self-contained */}
      <style>{`
        @keyframes fadeIn { from { opacity:0; transform: translateY(4px); } to { opacity:1; transform: translateY(0); } }
        @keyframes vupp-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes stripe { from { background-position: 0 0; } to { background-position: 48px 0; } }
        @keyframes wiggle { 0%,100% { transform: rotate(-3deg); } 50% { transform: rotate(3deg); } }
        @keyframes shimmer { 0% { transform: translateX(-100%); } 100% { transform: translateX(200%); } }
        @keyframes antennaTwitch { 0%,90%,100% { transform: rotate(0); } 92% { transform: rotate(-12deg); } 96% { transform: rotate(10deg); } }
      `}</style>
    </div>
  );
}

/** Big retro TV face — eyes blink, antenna twitches, screen shimmers. */
function TvMascot() {
  return (
    <div className="inline-block relative" style={{ animation: "wiggle 2400ms ease-in-out infinite" }}>
      {/* Antennae */}
      <div className="absolute -top-7 left-1/2 -translate-x-1/2 flex gap-8">
        <span
          className="block w-[3px] h-7 bg-orange-300 rounded origin-bottom"
          style={{
            transform: "rotate(-22deg)",
            animation: "antennaTwitch 3.2s ease-in-out infinite",
          }}
        />
        <span
          className="block w-[3px] h-7 bg-orange-300 rounded origin-bottom"
          style={{
            transform: "rotate(22deg)",
            animation: "antennaTwitch 3.6s ease-in-out infinite",
            animationDelay: "1s",
          }}
        />
      </div>
      {/* Antenna balls */}
      <div className="absolute -top-9 left-1/2 -translate-x-1/2 flex gap-8">
        <span className="block w-2 h-2 rounded-full bg-orange-400 shadow-[0_0_12px_rgba(244,114,52,0.8)]" />
        <span className="block w-2 h-2 rounded-full bg-orange-400 shadow-[0_0_12px_rgba(244,114,52,0.8)]" />
      </div>
      {/* TV body */}
      <div className="w-32 h-24 sm:w-40 sm:h-28 rounded-2xl bg-gradient-to-br from-slate-800 to-slate-900 border-4 border-slate-700 shadow-2xl ring-2 ring-orange-500/20 relative overflow-hidden">
        {/* Screen */}
        <div className="absolute inset-2 rounded-lg bg-gradient-to-br from-cyan-300 via-orange-300 to-fuchsia-400 overflow-hidden">
          {/* Static-line shimmer sweeping across screen */}
          <div
            className="absolute inset-y-0 -inset-x-1/2 bg-gradient-to-r from-transparent via-white/70 to-transparent"
            style={{ animation: "shimmer 1.6s linear infinite" }}
          />
          {/* CRT scanlines */}
          <div className="absolute inset-0 [background-image:repeating-linear-gradient(0deg,rgba(0,0,0,0.18)_0,rgba(0,0,0,0.18)_2px,transparent_2px,transparent_4px)]" />
          {/* Eyes */}
          <div className="absolute inset-0 flex items-center justify-center gap-3 sm:gap-4">
            <Eye delay="0s" />
            <Eye delay="0.3s" />
          </div>
        </div>
      </div>
      {/* TV stand */}
      <div className="w-12 h-2 mx-auto mt-1 rounded-full bg-white/20" />
    </div>
  );
}

// Cute little animated eye — used inside the TV screen while loading.
function Eye({ delay }) {
  return (
    <span
      className="relative block w-2 h-3 rounded-full bg-white/90"
      style={{ animation: `blink 3.6s ${delay} infinite ease-in-out` }}
    >
      <span className="absolute top-0 left-0 w-1 h-1 rounded-full bg-white" />
      <style>{`
        @keyframes blink {
          0%, 92%, 100% { transform: scaleY(1); }
          94% { transform: scaleY(0.1); }
        }
      `}</style>
    </span>
  );
}